import { ImgForPath } from "../../../lib/images";
import tokens from "../../tokens.js";
import styles from "./avatar.css";

type AvatarSize = "80px" | "50px" | "40px" | "30px" | "24px";

type User = {
  id?: string;
  name?: string;
  profileImageData?: { path?: string } | null;
};

function getBackgroundColor(id: string | undefined) {
  if (!id) return tokens.color.core.primary;
  const short = id.substring(2, 7);
  const colorNumber = [...short].reduce((a, char) => a + char.charCodeAt(0), 0);
  const hue = colorNumber % 360;

  return `hsl(${hue}, 55%, 55%)`;
}

function getInitials(name: string | undefined) {
  return (name?.[0] || "?").toUpperCase();
}

function FallbackImage({ user: { id, name } }: { user: User }) {
  const backgroundColor = getBackgroundColor(id);
  const initials = getInitials(name);

  return (
    <div
      className={styles.fallbackImage}
      style={{ backgroundColor: backgroundColor }}
    >
      {initials}
    </div>
  );
}

function ProfileImage({ user, size }: { user: User; size: AvatarSize }) {
  const { profileImageData } = user;

  if (!profileImageData?.path) return <FallbackImage user={user} />;

  const width = size.replace("px", "");

  return (
    <ImgForPath
      width={width}
      height={width}
      path={profileImageData.path}
      className={styles.profileImage}
    />
  );
}

type AvatarProps = {
  user: User;
  size?: AvatarSize;
  highlight?: boolean;
};

/**
 * An Avatar represents an user as an image. Either by displaying the first
 * letter of the username with a random background color or by showing the users
 * profile image. When the user does not have a name, then a question mark is
 * used instead.
 */
function Avatar({ user, size = "50px", highlight = false }: AvatarProps) {
  return (
    <div className={styles.avatar({ highlight, size })}>
      <ProfileImage user={user} size={size} />
    </div>
  );
}

export default Avatar;
